import { HighLightsData } from "pages/sales-forecast/salesForecastTypes";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { TabPanel, TabView } from "primereact/tabview";

import React, { FC, forwardRef } from "react";
import { formatDateCards } from "utils/salesForecastUtils";

interface Card {
  highLightsData: HighLightsData;
}

// function formatDate(dateString: string): string {
//   const date = new Date(dateString);

//   // Array of month names
//   const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

//   // Extract day, month, and year
//   const day = date.getDate();
//   const month = months[date.getMonth()];
//   const year = date.getFullYear().toString().slice(-2); // Get last two digits of year

//   return `${day} ${month}'${year}`;
// }

const formatNumber = (value: number) => {
  const numString = value.toString(); // Convert number to string

  if (numString.length > 6) {
    // If the number is more than 6 digits, represent it in millions (M)
    return Number(numString.slice(0, -6)).toLocaleString("en-US") + "M";
  } else if (numString.length > 4) {
    // If the number is more than 4 digits, represent it in thousands (K)
    return Number(numString.slice(0, -3)).toLocaleString("en-US") + "K";
  } else {
    // If it's a small number, just return it formatted normally
    return value.toLocaleString("en-US");
  }
};

const Card = forwardRef<HTMLDivElement, Card>(({ highLightsData }, ref) => {
  const formattedTotalForecastQT = formatNumber(
    highLightsData?.total_forecast_quantity
  );
  const formattedNextTotalForecastQT = formatNumber(
    highLightsData?.total_next_30_days_forecast
  );
  const formattedPreviousTotalForecastQT = formatNumber(
    highLightsData?.total_previous_year_30_days_history
  );

  return (
    <section className="highlights-section">
      {/* <h5>Forecast Highlights</h5> */}
      <div className="highlights-box-row" ref={ref}>
        <div className="highlights-box-col">
          <div className="highlights-box-inner top-col">
            <strong className="primary">$ {formattedTotalForecastQT} </strong>
            {/* <span className='green'>{highLightsData?.percentage_difference}</span> */}
            <p>
              Sales forecast for{" "}
              <b>
                {formatDateCards(highLightsData?.startDate)} to{" "}
                {formatDateCards(highLightsData?.endDate)}
              </b>
            </p>
          </div>
          <div className="bottom-col">
            <div className="highlights-box-inner bottom-col top-box">
              <strong style={{color: "#FEB019"}}>
                $ {formattedPreviousTotalForecastQT}
              </strong>
              <p>
                Sales forecast for previous year <b>30 days</b>
              </p>
            </div>
            <div className="highlights-box-inner bottom-col bottom-box">

              <div className="header-text">
                <strong className={highLightsData.forecast_arrow === "↓" ? "red"
                    : highLightsData.forecast_arrow === "↑" ? "green": ""}>$ {formattedNextTotalForecastQT} </strong>
              <span
                className={`forecast-percentage ${highLightsData.forecast_arrow === "↓" ? "red"
                    : highLightsData.forecast_arrow === "↑" ? "green": ""}`
                }
              >
                {highLightsData.forcast_percentage}
                {highLightsData.forecast_arrow}
              </span>
              </div>
              
              <p>
                Sales forecast for next <br/> <b>30 days</b>
              </p>
            </div>
          </div>
        </div>
        <div className="highlights-box-col">
          <div className="highlights-box-inner cities-col">
            <TabView>
              <TabPanel className="primary" header="Top Countries">
                <div className="custom-table">
                  <table>
                    <tbody>
                      {highLightsData.top_countries.map(
                        (item: any, index: number) => {
                          return (
                            <tr key={index}>
                              <td>{item.country_name}</td>
                              <td>{item.country_forecast_quantity}</td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </TabPanel>
              <TabPanel className="primary" header="Top States">
                <div className="custom-table">
                  <table>
                    <tbody>
                      {highLightsData.top_states.map(
                        (item: any, index: number) => {
                          return (
                            <tr key={index}>
                              <td>{item.state_name}</td>
                              <td>{item.state_forecast_quantity}</td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </TabPanel>
              <TabPanel className="primary" header="Top Cities">
                <div className="custom-table">
                  <table>
                    <tbody>
                      {highLightsData.top_cities.map(
                        (item: any, index: number) => {
                          return (
                            <tr key={index}>
                              <td>{item.city_name}</td>
                              <td>{item.city_forecast_quantity}</td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </TabPanel>
            </TabView>
          </div>
        </div>
        <div className="highlights-box-col">
          <div className="highlights-box-inner quarters-col">
            <strong className="primary">Quarters</strong>
            <div className="two-col-table">
              <div className="custom-table">
                <h5>History</h5>
                <table>
                  <tbody>
                    {highLightsData.history_quarters.map(
                      (item: any, index: number) => {
                        return (
                          <tr key={index}>
                            <td>{item.quarter}</td>
                            <td>{item.total_sales_quantity}</td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
              <div className="custom-table">
                <h5>Forecast</h5>
                <table>
                  <tbody>
                    {highLightsData.forecast_quarters.map(
                      (item: any, index: number) => {
                        const trendColor =
                          item.trend_arrow === "↓"
                            ? "red"
                            : item.trend_arrow === "↑"
                            ? "green"
                            : "";
                        return (
                          <tr key={index}>
                            <td>{item.quarter}</td>
                            <td>
                              {item.total_forecast_quantity}{" "}
                              <span className={trendColor}>
                                {item.percentage_difference}
                                {item.trend_arrow}{" "}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default Card;
